<template>
  <minimal-layout class="thank-you">
    <v-container class="content-container">
      <v-container fluid class="component-container-1400mw">
        <v-container class="pa-0" style="max-width: 1200px; min-height: 500px;">
          <v-card v-if="activeOrderState === null"
                  class="mx-auto"
                  rounded="0"
                  elevation="15"
                  style="padding: 1.5rem;"
          >
            <v-row class="d-flex justify-center align-center">
              <v-col cols="12" sm="1" class="d-flex justify-center">
                <v-icon size="55" color="success" icon="mdi-check"></v-icon>
              </v-col>
              <v-col cols="12" sm="11">
                <h3>{{ translation(translationList.order_thanks, 'Îți mulțumim pentru comanda ta!') }}</h3>
                {{
                  translation(translationList.order_please_wait_policy, 'Te rugăm să aștepți! În câteva secunde, polița ta va fi emisă!')
                }}
              </v-col>
            </v-row>
            <v-row class="mt-10 mb-5">
              <v-col cols="12" class="d-flex align-center justify-center">
                <v-progress-linear
                    indeterminate
                    color="primary"
                    striped
                    height="25"
                    class="slow-progress"
                ></v-progress-linear>
              </v-col>
            </v-row>
          </v-card>

          <v-card v-else-if="activeOrderState === orderStateCompleted"
                  class="mx-auto"
                  rounded="0"
                  elevation="15"
                  style="padding: 1.5rem;"
          >
            <v-row class="d-flex justify-center align-center">
              <v-col cols="12" sm="1" class="d-flex justify-center">
                <v-icon size="55" color="success" icon="mdi-check-all"></v-icon>
              </v-col>
              <v-col cols="12" sm="11">
                <h3>{{ translation(translationList.rca_policy_issued, 'Polița ta a fost emisă!') }}</h3>
                {{
                  translation(translationList.rca_policy_issued_helper, 'Poți descărca polița fie de aici, din pagină, folosind butonul de mai jos, fie din e-mailul pe care l-ai primit!')
                }}
              </v-col>
            </v-row>
            <v-row class="mt-10 mb-5">
              <v-col cols="12" class="d-flex align-center justify-center">
                <v-btn
                    class="block-on-mobile"
                    color="primary"
                    size="x-large"
                    flat
                    rounded="0"
                    @click="downloadRcaPolicyPdf()"
                >{{ translation(translationList.download_rca_policy, 'Descarcă polița RCA') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card>

          <v-card v-else
                  class="mx-auto"
                  rounded="0"
                  elevation="15"
                  style="padding: 1.5rem;"
          >
            <v-row class="d-flex justify-center align-center">
              <v-col cols="12" sm="1" class="d-flex justify-center">
                <v-icon size="55" color="error" icon="mdi-alert-rhombus"></v-icon>
              </v-col>
              <v-col cols="12" sm="11">
                <h3>{{ translation(translationList.something_went_wrong, 'Oops! Ceva nu a mers bine!') }}</h3>
                {{
                  translation(translationList.contact_us_problem, 'Te rugăm să ne contactezi pentru a rezolva problema!')
                }}
                <div v-if="order">
                  <p><strong>ID Comanda:</strong> {{ order.code }}</p>
                  <p><strong>Suma:</strong> {{ order.amount }}</p>
                  <p><strong>Creat la:</strong> {{ order.created_at }}</p>
                  <p><strong>Ultima actualizare la:</strong> {{ order.updated_at }}</p>
                  <p><strong>Status:</strong> {{ orderStates.find(state => state.index === order.state)?.name }}</p>
                </div>
              </v-col>
            </v-row>
            <v-row class="mt-10 mb-5">
              <v-col cols="12" class="d-flex align-center justify-center">
                <NuxtLink to="/contact-us" class="text-decoration-none color-white">
                  <v-btn
                      class="block-on-mobile"
                      color="primary"
                      size="x-large"
                      flat
                      rounded="0"
                      @click=""
                  >{{ translation(translationList.contact, 'Contact') }}
                  </v-btn>
                </NuxtLink>
              </v-col>
            </v-row>
          </v-card>
        </v-container>
      </v-container>
    </v-container>
  </minimal-layout>
</template>
<script lang="ts">
import {defineComponent, ref, onMounted, onBeforeMount} from 'vue';
import {useAuthStore} from "~/plugins/store/auth";
import {useOrderStore} from "~/plugins/store/order";
import type {Order, OrderState} from "~/types/Order";
import {useRcaStore} from "~/plugins/store/rca";
import {type RcaPolicy} from '~/types/Rca';
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import {useRoute} from "vue-router";
import MinimalLayout from "~/layouts/MinimalLayout.vue";

const ORDER_STATE_PLACED = 0;
const ORDER_STATE_COMPLETED = 2;
const ORDER_STATE_FAILED = 5;
const MAX_FETCH_ATTEMPTS = 20;
const FETCH_INTERVAL_MS = 5000;

export default defineComponent({
  components: {MinimalLayout},
  setup() {
    const orderId = ref<string | null>(null);
    const authStore = useAuthStore();
    const activeOrderState = ref<OrderState | null>(null);
    const orderStore = useOrderStore();
    const orderStates = ref<OrderState[]>([]);
    const orderStateCompleted = ref<OrderState | null>(null);
    const order = ref<Order | null>(null);
    const rcaStore = useRcaStore();
    const rcaPolicy = ref<RcaPolicy | null>(null);
    let accessToken = authStore.getAccessToken();
    const route = useRoute();
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({}); // Initialize translations as a reactive reference

    onBeforeMount(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      // Retrieve orderId from query params
      const urlParams = new URLSearchParams(window.location.search);
      orderId.value = urlParams.get('order_id') || '0';
      if (orderId.value === '0') {
        orderId.value = urlParams.get('orderId') || '0';
      }
      if (orderId.value === '0') {
        console.error('orderId is missing');
        await navigateTo('/');
        return;
      }

      let authToken = null;
      if (typeof route.query.auth_token === 'string' && route.query.auth_token.length > 0) {
        authToken = route.query.auth_token;
      }

      if (authToken) {
        await authStore.authUserByToken(authToken);
        accessToken = authStore.getAccessToken();
      }

      orderStates.value = await orderStore.getOrderStates();
      const tmpOrderStateCompleted = orderStates.value.find((state) => state.index === ORDER_STATE_COMPLETED);
      if (tmpOrderStateCompleted) {
        orderStateCompleted.value = tmpOrderStateCompleted;
      }
    });

    const downloadRcaPolicyPdf = async () => {
      if (accessToken && rcaPolicy.value && rcaPolicy.value.id) {
        const pdf = await rcaStore.fetchPolicyPdf(rcaPolicy.value.id, accessToken);
        const blob = new Blob([pdf], {type: 'application/pdf'});
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `polita_rca_${rcaPolicy.value.series + '_' + rcaPolicy.value.number}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    };

    const fetchOrderUntilStateChanges = async () => {
      let attempts = 0;
      const intervalId = setInterval(async () => {
        if (attempts >= MAX_FETCH_ATTEMPTS) {
          clearInterval(intervalId);
          activeOrderState.value = orderStates.value.find(state => state.index === ORDER_STATE_FAILED) || null;
          order.value = await orderStore.fetchOrder(<string>orderId.value, <string>accessToken);
          return;
        }

        const fetchedOrder = await orderStore.fetchOrder(<string>orderId.value, <string>accessToken);
        if (fetchedOrder.state !== ORDER_STATE_PLACED) {
          clearInterval(intervalId);
          activeOrderState.value = orderStates.value.find(state => state.index === fetchedOrder.state) || null;
          order.value = fetchedOrder;

          if (fetchedOrder.state === ORDER_STATE_COMPLETED) {
            if (fetchedOrder?.order_order_items) {
              for (const orderItem of fetchedOrder.order_order_items) {
                if (orderItem.rca_policy) {
                  rcaPolicy.value = orderItem.rca_policy;
                }
              }
            }
          }
          return;
        }

        attempts++;
      }, FETCH_INTERVAL_MS);
    };

    onMounted(() => {
      fetchOrderUntilStateChanges();
    });

    return {
      translationList,
      translation,
      activeOrderState,
      orderStateCompleted,
      order,
      rcaPolicy,
      downloadRcaPolicyPdf,
      orderStates
    };
  },
});
</script>
<style scoped>
</style>
